import { FC, useEffect, useState, useMemo } from 'react';
import useSWR from 'swr';
import { IInternalToolInformationTab, IInspectionField, IUpdateToolInspectionDTO } from './toolInfoTab.types';
import { displaySuccessNotification, displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import ToolConditionsPanel from './components/ToolConditionsPanel/ToolConditionsPanel';
import ActionButtons from './components/ActionButtons/ActionButtons';
import ToolInfoTabSkeleton from './components/ToolInfoTabSkeleton/ToolInfoTabSkeleton';
import { NudosStaticAlertInComponent } from 'components/NudosComponents';
import { IalertOptions } from 'components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import ToolEvidenceCarousel from './components/ToolEvidenceCarousel/ToolEvidenceCarousel';
import { IToolEvidence } from './components/ToolEvidenceCarousel/toolEvidenceCarousel.types';
import { getToolInformation, updateToolInspection } from 'services/toolsModule';
import { LOCATION_PLACE_TYPE } from 'types/services';
import './ToolInfoTab.scss';

/**
 * Componente que muestra la información física de la herramienta
 * @param productId - ID del producto
 */
const ToolInfoTab: FC<ToolInfoTabProps> = ({ productId }) => {
  const [originalFields, setOriginalFields] = useState<IInspectionField[]>([]);
  const [fields, setFields] = useState<IInspectionField[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: toolInformation,
    isLoading: isLoadingData,
    mutate
  } = useSWR<IInternalToolInformationTab>(productId ? `tool-information-${productId}` : null, () =>
    getToolInformation(productId?.toString() || '')
  );

  const { locationPlace, lastInspection } = toolInformation || {};
  const { fields: inspectionFields, images: inspectionImages } = lastInspection ?? {};
  const isWarehouseLocation = locationPlace === LOCATION_PLACE_TYPE.NUDOS;
  const existsInspectionFields = !!lastInspection && !!inspectionFields?.length;
  const existsInspectionImages = !lastInspection || !inspectionImages?.length; 

  const evidences: IToolEvidence[] = useMemo(() => {
    if (!inspectionImages) return [];
    return inspectionImages?.map(image => ({
      id: image?.id,
      imageUrl: image?.url
    }));
  }, [toolInformation]);

  const alertOptions: IalertOptions = {
    title: (
      <span>
        Esta vista muestra solo el estado actual de la herramienta. Para consultar reportes anteriores del Device Status
        Notification (DSN), dirígete al Historial.
      </span>
    )
  };

  const handleFieldsChange = (updatedFields: IInspectionField[]) => {
    setFields(updatedFields);
  };

  const handleCancelChanges = () => {
    setFields(originalFields);
    setHasChanges(false);
  };

  const handleSaveChanges = async () => {
    if (!productId) return;

    setIsLoading(true);
    try {
      const inspectionData: IUpdateToolInspectionDTO = {
        productId: productId,
        fields: fields?.map(field => ({
          inspectionFieldId: field?.id,
          value: field?.value
        }))
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(inspectionData));

      await updateToolInspection(formData);

      setOriginalFields(fields);
      setHasChanges(false);

      mutate();

      displaySuccessNotification({
        customJSXMessage: <>La información de la herramienta se modificó exitosamente</>
      });
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar las condiciones de la herramienta</>
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkForChanges = () => {
    const fieldsChanged = JSON?.stringify(fields) !== JSON?.stringify(originalFields);
    setHasChanges(fieldsChanged);
  };

  const handleUpdateFields = () => {
    if (!inspectionFields?.length) return;
    setOriginalFields(inspectionFields);
    setFields(inspectionFields);
  };

  useEffect(() => {
    handleUpdateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolInformation]);

  useEffect(() => {
    checkForChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  if (isLoadingData) {
    return <ToolInfoTabSkeleton />;
  }

  if (!toolInformation) {
    return (
      <div className="toolInfoTab">
        <div>No se pudo cargar la información de la herramienta</div>
      </div>
    );
  }

  return (
    <div className="toolInfoTab">
      <div>
        {existsInspectionFields && (
          <NudosStaticAlertInComponent
            customClassname={`toolInfoAlertMessage customNudosStaticAlertInComponentContainer`}
            type="information"
            alertOptions={alertOptions}
          />
        )}
        {existsInspectionFields && (
          <ToolConditionsPanel
            fields={fields}
            onFieldsChange={handleFieldsChange}
            productId={productId}
            disabled={!isWarehouseLocation}
            isWarehouseLocation={isWarehouseLocation}
            disableDownload={hasChanges}
          />
        )}
      </div>
      <ToolEvidenceCarousel
        evidences={evidences}
        title="Fotos de la herramienta (Evidencia)"
        maxVisible={5}
        showEmptyState={existsInspectionImages}
        disabled={hasChanges}
      />
      {hasChanges && <ActionButtons onCancel={handleCancelChanges} onSave={handleSaveChanges} isLoading={isLoading} />}
    </div>
  );
};

interface ToolInfoTabProps {
  productId?: number;
}

export default ToolInfoTab;
